// client/src/coms/components/StickerManager.js
// Settings → Stickers:
//   My stickers   packs you can use but do not own (installed, bought, shared, this server's)
//   My packs      your packs: local (this device) and on Voxemera Server
//   Create sticker / Create pack   with the hosting choice (Local by default, or Voxemera Server)
//   Install by code               share code, pack ID, or a pack file
//   Browse Marketplace
// Server packs are never copied: installing, sharing and server packs all reference the creator's original.
// Local packs live on this device (stickers/localStickerStore.js) until moved to the server.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { comsApiGet, comsApiPost } from '../comsApi';
import { STICKERS_CHANGED_EVENT, notifyStickersChanged } from '../stickers/stickerSuggest';
import useStickerSuggestionsEnabled, { writeStickerSuggestionsEnabled } from '../stickers/stickerSuggestPrefs';
import { createLocalPack, listLocalPacks, updateLocalPack } from '../stickers/localStickerStore';
import HostingSelector from './HostingSelector';
import LocalPackCard, { localUploadOne } from './LocalPackCard';
import PackBrowser from './PackBrowser';
import PackInfoDialog from './PackInfoDialog';
import StickerPackCard from './StickerPackCard';
import StickerUploader from './StickerUploader';
import TrustDialog from './TrustDialog';

const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '8px 10px' };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

const SECTIONS = [
  { key: 'installed', title: 'Installed', empty: 'Free packs you add (from a share code or the Marketplace) appear here.' },
  { key: 'shared', title: 'Shared with me and bought', empty: 'Packs that friends shared with you, and packs you buy, appear here.' },
];

const NAV = [
  { key: 'stickers', label: 'My stickers' },
  { key: 'packs', label: 'My packs' },
  { key: 'create-sticker', label: 'Create sticker' },
  { key: 'create-pack', label: 'Create pack' },
  { key: 'code', label: 'Install by code' },
  { key: 'market', label: 'Browse Marketplace' },
];

export function openMarketplace() {
  try {
    window.dispatchEvent(new CustomEvent('vox-open-marketplace'));
  } catch {}
}

function CreatePackPanel({ onCreated, onError }) {
  const [hosting, setHosting] = useState('local');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [busy, setBusy] = useState(false);
  async function create() {
    setBusy(true);
    try {
      if (hosting === 'local') await createLocalPack({ name, description });
      else await comsApiPost('/stickers/packs', { name, description, visibility });
      setName('');
      setDescription('');
      onCreated(hosting);
    } catch (e) {
      onError(errText(e, 'Could not create the pack.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="vox-sticker-toolbar vox-sticker-form" data-testid="create-pack-panel">
      <div style={{ fontSize: 13, fontWeight: 900, width: '100%' }}>Create a pack</div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pack name" maxLength={40} aria-label="New pack name" style={{ ...field, flex: '1 1 220px' }} />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" maxLength={200} rows={2} aria-label="Pack description" style={{ ...field, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
      <HostingSelector value={hosting} onChange={setHosting} name="new-pack-hosting" disabled={busy} />
      {hosting === 'server' ? (
        <label style={{ width: '100%', fontSize: 12, fontWeight: 800 }}>
          Who can use it
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} style={{ ...field, width: '100%', marginTop: 4 }} aria-label="Who can use it">
            <option value="private">Private: only me and accounts I share it with</option>
            <option value="friends">Friends: my friends can add it with the share code</option>
            <option value="unlisted">Anyone with the share code</option>
          </select>
          <span style={{ display: 'block', fontWeight: 400, opacity: 0.7, marginTop: 4 }}>Add stickers first, then publish it in the Marketplace from My packs.</span>
        </label>
      ) : (
        <div style={{ fontSize: 12, opacity: 0.7, width: '100%' }}>Only you can see local packs. Stickers you send from them still appear for the people you send them to. You can move the pack to Voxemera Server later.</div>
      )}
      <button type="button" className="coms-mini-btn primary" disabled={busy || name.trim().length < 2} onClick={create} data-testid="create-pack">
        {busy ? 'Creating…' : hosting === 'local' ? 'Create local pack' : 'Create pack on Voxemera Server'}
      </button>
    </div>
  );
}

function CreateStickerPanel({ localPacks, serverPacks, onChanged, onError }) {
  const [hosting, setHosting] = useState('local');
  const editable = useMemo(() => (hosting === 'local' ? localPacks.filter((p) => !p.server_pack_id) : serverPacks.filter((p) => p.can_edit)), [hosting, localPacks, serverPacks]);
  const [packId, setPackId] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const pack = editable.find((p) => String(p.id) === String(packId)) || null;

  // Keep the chosen pack while it exists in the chosen hosting; otherwise pick the first (or a new one).
  useEffect(() => {
    if (packId === 'new' || editable.some((p) => String(p.id) === String(packId))) return;
    setPackId(editable[0] ? String(editable[0].id) : 'new');
  }, [editable, packId]);

  async function createPack() {
    setBusy(true);
    try {
      const created = hosting === 'local' ? await createLocalPack({ name: newName }) : (await comsApiPost('/stickers/packs', { name: newName, visibility: 'private' })).pack;
      setNewName('');
      await onChanged();
      setPackId(String(created.id));
    } catch (e) {
      onError(errText(e, 'Could not create the pack.'));
    } finally {
      setBusy(false);
    }
  }

  const count = pack ? (hosting === 'local' ? pack.stickers.length : pack.sticker_count || 0) : 0;
  return (
    <div className="vox-sticker-toolbar vox-sticker-form" data-testid="create-sticker-panel">
      <div style={{ fontSize: 13, fontWeight: 900, width: '100%' }}>Create stickers</div>
      <HostingSelector value={hosting} onChange={setHosting} name="new-sticker-hosting" disabled={busy} />
      <label style={{ width: '100%', fontSize: 12, fontWeight: 800 }}>
        Pack
        <select value={packId} onChange={(e) => setPackId(e.target.value)} style={{ ...field, width: '100%', marginTop: 4 }} aria-label="Pack for the new stickers">
          {editable.map((p) => <option key={p.id} value={String(p.id)}>{p.name}{p.visibility === 'server' ? ` (${p.server_name || 'server'})` : ''}</option>)}
          <option value="new">+ New pack…</option>
        </select>
      </label>
      {packId === 'new' || !pack ? (
        <>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={hosting === 'local' ? 'New local pack name' : 'New server pack name'} maxLength={40} aria-label="New pack name" style={{ ...field, flex: '1 1 220px' }} />
          <button type="button" className="coms-mini-btn primary" disabled={busy || newName.trim().length < 2} onClick={createPack}>{busy ? 'Creating…' : 'Create pack'}</button>
        </>
      ) : (
        <div style={{ width: '100%' }}>
          <StickerUploader
            key={`${hosting}:${pack.id}`}
            pack={pack}
            roomLeft={Math.max(0, 60 - count)}
            uploadOne={hosting === 'local' ? localUploadOne(pack) : null}
            destination={hosting === 'local' ? 'saved on this device' : 'stored on Voxemera Server'}
            onClose={() => setPackId(String(pack.id))}
            onUploaded={onChanged}
          />
        </div>
      )}
    </div>
  );
}

export default function StickerManager({ serverKey = '', serverName = '', initialSection = 'packs' }) {
  const [section, setSection] = useState(initialSection);
  const [packs, setPacks] = useState([]);
  const [localPacks, setLocalPacks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [info, setInfo] = useState(null); // pack detail for PackInfoDialog
  const [trust, setTrust] = useState(null); // import confirmation
  const suggestionsEnabled = useStickerSuggestionsEnabled();

  const loadLocal = useCallback(async () => {
    setLocalPacks(await listLocalPacks().catch(() => []));
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await comsApiGet(`/stickers/packs${serverKey ? `?server=${encodeURIComponent(serverKey)}` : ''}`);
      const list = Array.isArray(r && r.packs) ? r.packs : [];
      setPacks(list);
      // A local pack whose server copy was deleted can be used (and moved) again.
      const mineOnServer = new Set(list.filter((p) => p.is_mine).map((p) => p.id));
      for (const lp of await listLocalPacks().catch(() => [])) {
        if (lp.server_pack_id && !mineOnServer.has(lp.server_pack_id)) await updateLocalPack(lp.id, { server_pack_id: 0, moved_at: '' }).catch(() => {});
      }
    } catch (e) {
      setErr(errText(e, 'Could not load sticker packs.'));
    } finally {
      setLoaded(true);
    }
    await loadLocal();
  }, [loadLocal, serverKey]);

  useEffect(() => {
    load();
  }, [load]);

  // Local changes (from cards, the composer's favourites, another tab of this view) refresh the list.
  useEffect(() => {
    const onChanged = () => loadLocal();
    window.addEventListener(STICKERS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(STICKERS_CHANGED_EVENT, onChanged);
  }, [loadLocal]);

  const reload = useCallback(async () => {
    await load();
    notifyStickersChanged();
  }, [load]);

  async function act(fn, fallback) {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await reload();
    } catch (e) {
      setErr(errText(e, fallback));
    } finally {
      setBusy(false);
    }
  }

  async function lookup() {
    setErr('');
    try {
      const r = await comsApiGet(`/stickers/lookup?q=${encodeURIComponent(code.trim())}`);
      setInfo(r.pack);
    } catch (e) {
      setErr(errText(e, 'No pack found for that code or ID.'));
    }
  }

  async function chooseImportFile(file) {
    setErr('');
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) return setErr('That file is too large to be a sticker pack.');
    let doc;
    try {
      doc = JSON.parse(await file.text());
    } catch {
      return setErr('That file is not a Voxemera sticker pack.');
    }
    if (!doc || doc.format !== 'voxemera-sticker-pack') return setErr('That file is not a Voxemera sticker pack.');
    const stickers = Array.isArray(doc.stickers) ? doc.stickers : [];
    const bytes = stickers.reduce((n, s) => n + Math.floor((String((s && s.data) || '').length * 3) / 4), 0);
    setTrust({
      info: { name: doc.name, creator: 'From a file (becomes your own new pack)', count: stickers.length, bytes, types: Array.from(new Set(stickers.map((s) => String((s && s.mime) || 'unknown')))) },
      run: () => comsApiPost('/stickers/import', { consent: true, pack: doc }),
    });
  }

  const bySection = (key) => packs.filter((p) => p.section === key);
  const mine = bySection('mine');
  const serverPacks = bySection('server');
  const myCount = mine.length + localPacks.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, borderRadius: 12, border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))', background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))', cursor: 'pointer' }}>
        <input type="checkbox" checked={suggestionsEnabled} onChange={(e) => writeStickerSuggestionsEnabled(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          <span style={{ fontWeight: 800, fontSize: 13 }}>Suggest stickers while typing</span>
          <span style={{ display: 'block', fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            When a word you type matches a sticker’s keywords, matching stickers appear above the message box. Click one to send it; the rest of your message stays. To set keywords, click a sticker in My packs (🔍 shows it full size).
          </span>
        </span>
      </label>

      <nav className="vox-sticker-nav" aria-label="Stickers">
        {NAV.map((n) => (
          <button key={n.key} type="button" className={`vox-sticker-nav-btn ${section === n.key ? 'active' : ''}`} aria-current={section === n.key ? 'page' : undefined} onClick={() => setSection(n.key)} data-testid={`sticker-nav-${n.key}`}>
            {n.label}
            {n.key === 'packs' && myCount ? <span className="vox-sticker-nav-count">{myCount}</span> : null}
          </button>
        ))}
      </nav>

      {err ? <div className="coms-form-error" role="alert">{err}</div> : null}
      {notice ? <div role="status" style={{ fontSize: 12, color: '#a7f3d0' }}>{notice}</div> : null}

      {section === 'packs' ? (
        <>
          <section aria-label="Local packs" className="vox-sticker-section">
            <div className="vox-sticker-section-title">On this device <span style={{ opacity: 0.5 }}>{localPacks.length}</span></div>
            {localPacks.length ? localPacks.map((p) => <LocalPackCard key={p.id} pack={p} onChanged={reload} onError={setErr} />) : <div className="vox-sticker-empty">Local packs are free and stay on this device. Create one in “Create pack”.</div>}
          </section>
          <section aria-label="Packs on Voxemera Server" className="vox-sticker-section">
            <div className="vox-sticker-section-title">On Voxemera Server <span style={{ opacity: 0.5 }}>{mine.length}</span></div>
            {mine.length ? mine.map((p) => <StickerPackCard key={p.id} pack={p} onChanged={reload} onError={setErr} />) : loaded ? <div className="vox-sticker-empty">Server packs can be shared with a code and published in the Marketplace. Create one, or move a local pack.</div> : null}
          </section>
        </>
      ) : null}

      {section === 'stickers' ? (
        <>
          {SECTIONS.map((sec) => {
            const list = bySection(sec.key);
            return (
              <section key={sec.key} aria-label={sec.title} className="vox-sticker-section">
                <div className="vox-sticker-section-title">{sec.title} <span style={{ opacity: 0.5 }}>{list.length}</span></div>
                {list.length ? list.map((p) => <StickerPackCard key={p.id} pack={p} onChanged={reload} onError={setErr} />) : loaded ? <div className="vox-sticker-empty">{sec.empty}</div> : null}
              </section>
            );
          })}
          <section aria-label="Server packs" className="vox-sticker-section">
            <div className="vox-sticker-section-title">Server packs{serverName ? ` · ${serverName}` : ''} <span style={{ opacity: 0.5 }}>{serverPacks.length}</span></div>
            {!serverKey ? (
              <div className="vox-sticker-empty">Open a server to see the packs its members can use there.</div>
            ) : serverPacks.length ? (
              serverPacks.map((p) => <StickerPackCard key={p.id} pack={p} onChanged={reload} onError={setErr} />)
            ) : loaded ? (
              <div className="vox-sticker-empty">This server has no sticker packs yet. Server admins add them in Server settings → Stickers.</div>
            ) : null}
          </section>
        </>
      ) : null}

      {section === 'create-sticker' ? <CreateStickerPanel localPacks={localPacks} serverPacks={mine.concat(serverPacks)} onChanged={reload} onError={setErr} /> : null}

      {section === 'create-pack' ? (
        <CreatePackPanel
          onError={setErr}
          onCreated={async (where) => {
            await reload();
            setNotice(where === 'local' ? 'Local pack created. Add stickers to it below.' : 'Pack created on Voxemera Server. Add stickers to it below.');
            setSection('packs');
          }}
        />
      ) : null}

      {section === 'code' ? (
        <div className="vox-sticker-toolbar">
          <div style={{ fontSize: 13, fontWeight: 900, width: '100%' }}>Install by code</div>
          <div style={{ fontSize: 12, opacity: 0.7, width: '100%' }}>Enter a share code (VX-STK-…) or a pack ID to see the pack and add it. Paid packs must be bought first.</div>
          <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && code.trim() && lookup()} placeholder="Share code (VX-STK-…) or pack ID" aria-label="Share code or pack ID" style={{ ...field, flex: '1 1 220px' }} />
          <button type="button" className="coms-mini-btn primary" disabled={!code.trim()} onClick={lookup}>View pack…</button>
          <label className="coms-mini-btn" style={{ cursor: 'pointer' }}>
            Import file…
            <input type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={(e) => { chooseImportFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
          </label>
        </div>
      ) : null}

      {section === 'market' ? (
        <div className="vox-sticker-section">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', fontSize: 12, opacity: 0.75 }}>Sticker packs made by the community. Free packs are added straight away.</div>
            <button type="button" className="coms-mini-btn" onClick={openMarketplace}>Open the Marketplace</button>
          </div>
          <PackBrowser pageSize={12} onChanged={reload} />
        </div>
      ) : null}

      {info ? <PackInfoDialog pack={info} onClose={() => setInfo(null)} onChanged={() => { setCode(''); reload(); }} /> : null}
      {trust ? (
        <TrustDialog
          title="Import sticker pack?"
          info={{ ...trust.info, permissions: 'None (images only)' }}
          busy={busy}
          onCancel={() => setTrust(null)}
          onConfirm={() =>
            act(async () => {
              await trust.run();
              setTrust(null);
            }, 'Import failed.')
          }
        />
      ) : null}
    </div>
  );
}
